import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Copy,
  ExternalLink,
  RefreshCw,
  Send,
} from "lucide-react";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const getStatusConfig = (t) => {
  const defaultConfig = {
    success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Success" },
    pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
    processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: "Processing" },
    failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Failed" },
    expired: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "Expired" },
    cancelled: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "Cancelled" },
    declined: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Declined" },
  };

  if (!t) return defaultConfig;

  return {
    success: { ...defaultConfig.success, label: t("success") },
    pending: { ...defaultConfig.pending, label: t("pending") },
    processing: { ...defaultConfig.processing, label: t("processing") },
    failed: { ...defaultConfig.failed, label: t("failed") },
    expired: { ...defaultConfig.expired, label: t("expired") },
    cancelled: { ...defaultConfig.cancelled, label: t("cancelled") },
    declined: { ...defaultConfig.declined, label: t("declined") || "Declined" },
  };
};

export default function TransactionDetail({ transaction, open, onClose, onRetry, onResendCallback, isQRCode = false }) {
  const { t } = useLanguage();
  
  if (!transaction) return null;

  const statusKey = (transaction?.status || '').toLowerCase();
  const status = getStatusConfig(t)[statusKey] || getStatusConfig(t).pending;
  const StatusIcon = status.icon;

  // Helper function to translate transaction status
  const translateStatus = (statusValue) => {
    if (!statusValue) return "N/A";
    const statusLower = statusValue.toLowerCase();
    const statusConfig = getStatusConfig(t);
    return statusConfig[statusLower]?.label || statusValue;
  };

  // Helper function to translate callback status
  const translateCallbackStatus = (callbackStatus) => {
    if (!callbackStatus) return "N/A";
    const statusLower = callbackStatus.toLowerCase();
    if (statusLower === "success" || statusLower === "delivered") return t("delivered") || "Delivered";
    if (statusLower === "failed") return t("failed") || "Failed";
    if (statusLower === "pending") return t("pending") || "Pending";
    return callbackStatus;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const InfoRow = ({ label, value, copyable = false, mono = false }) => (
    <div className="flex items-start justify-between py-3">
      <span className="text-sm text-slate-500">{label}</span>
      <div className="flex items-center gap-2">
        <span className={`text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>
          {value || "N/A"}
        </span>
        {copyable && value && (
          <button onClick={() => copyToClipboard(value)}>
            <Copy className="h-3 w-3 text-slate-400 hover:text-slate-600" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                transaction.type === "deposit" ? "bg-blue-100" : "bg-emerald-100"
              }`}
            >
              {transaction.type === "deposit" ? (
                <ArrowDownToLine className="h-6 w-6 text-blue-600" />
              ) : (
                <ArrowUpFromLine className="h-6 w-6 text-emerald-600" />
              )}
            </div>
            <div>
              <SheetTitle className="text-left">{isQRCode ? (t("qrCodeDetails") || "QR Code Details") : t("transactionDetails")}</SheetTitle>
              <p className="text-sm text-slate-500 font-mono mt-0.5">
                {isQRCode ? transaction.payment_id : transaction.transaction_id}
              </p>
            </div>
          </div>
        </SheetHeader>

        {/* Status Banner */}
        <div className={`${status.color} rounded-xl p-4 mb-6`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StatusIcon className={`h-5 w-5 ${transaction.status === "processing" ? "animate-spin" : ""}`} />
              <span className="font-medium">{status.label}</span>
            </div>
            <span className="text-2xl font-bold text-blue-700">
              +฿{isQRCode ? (transaction.amount?.toLocaleString()) : (transaction.amount?.toLocaleString())}
            </span>
          </div>
          {!isQRCode && transaction.error_message && (
            <p className="text-sm mt-2 opacity-80">{transaction.error_message}</p>
          )}
        </div>

        {/* Transaction/QR Code Info */}
        <div className="space-y-6">
          {isQRCode ? (
            <>
              <div>
                <h3 className="font-medium text-slate-900 mb-2">{t("qrCodeInfo") || "QR Code Info"}</h3>
                <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-100">
                  <InfoRow label={t("paymentId") || "Payment ID"} value={transaction.payment_id} copyable mono />
                  {transaction.reference && <InfoRow label={t("reference")} value={transaction.reference} copyable mono />}
                  <InfoRow label={t("currency")} value={transaction.currency || "THB"} />
                  <InfoRow label={t("status")} value={translateStatus(transaction.status)} />
                </div>
              </div>

              <div>
                <h3 className="font-medium text-slate-900 mb-2">{t("processingInfo")}</h3>
                <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-100">
                  <InfoRow label={t("merchant")} value={transaction.merchant?.name || transaction.merchant?.code || ""} />
                  <InfoRow label={t("provider")} value={transaction.client_bank_name || ""} />
                  {transaction.client_name && <InfoRow label={t("clientName") || "Client Name"} value={transaction.client_name} />}
                  {transaction.client_bank_account && <InfoRow label={t("clientBankAccount") || "Client Bank Account"} value={transaction.client_bank_account} copyable mono />}
                </div>
              </div>

              {transaction.expires_at && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">{t("expiryInfo") || "Expiry Info"}</h3>
                  <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-100">
                    <InfoRow 
                      label={t("expiresAt") || "Expires At"} 
                      value={transaction.expires_at ? format(new Date(transaction.expires_at), "MMM d, yyyy HH:mm:ss") : "N/A"} 
                    />
                  </div>
                </div>
              )}

              <div>
                <h3 className="font-medium text-slate-900 mb-2">{t("timestamps")}</h3>
                <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-100">
                  <InfoRow 
                    label={t("createdAt")} 
                    value={transaction.createdAt ? format(new Date(transaction.createdAt), "MMM d, yyyy HH:mm:ss") : "N/A"} 
                  />
                  <InfoRow 
                    label={t("updatedAt")} 
                    value={transaction.updatedAt ? format(new Date(transaction.updatedAt), "MMM d, yyyy HH:mm:ss") : "N/A"} 
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <h3 className="font-medium text-slate-900 mb-2">{t("transactionInfo")}</h3>
                <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-100">
                  <InfoRow label={t("transactionId")} value={transaction.transaction_id} copyable mono />
                  <InfoRow label={t("merchantRef")} value={transaction.merchant_ref} copyable mono />
                  <InfoRow label={t("type")} value={transaction.type === "deposit" ? t("deposit") : t("withdrawal")} />
                  <InfoRow label={t("paymentMethod")} value={transaction.payment_method?.replace(/_/g, " ")} />
                  <InfoRow label={t("currency")} value={transaction.currency || "THB"} />
                  <InfoRow label={t("feeAmount")} value={`฿${transaction.fee_amount?.toLocaleString() || 0}`} />
                  <InfoRow label={t("netAmount")} value={`฿${transaction.net_amount?.toLocaleString() || transaction.amount?.toLocaleString()}`} />
                </div>
              </div>

              <div>
                <h3 className="font-medium text-slate-900 mb-2">{t("processingInfo")}</h3>
                <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-100">
                  <InfoRow label={t("merchant")} value={transaction.merchant_code} />
                  <InfoRow label={t("provider")} value={transaction.provider_code} />
                  <InfoRow label={t("providerRef")} value={transaction.provider_ref} copyable mono />
                  <InfoRow label={t("routingAttempts")} value={transaction.routing_attempts || 1} />
                </div>
              </div>

              <div>
                <h3 className="font-medium text-slate-900 mb-2">{t("customerInfo")}</h3>
                <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-100">
                  <InfoRow label={t("customerId")} value={transaction.customer_id} copyable />
                  <InfoRow label={t("name")} value={transaction.customer_name} />
                  <InfoRow label={t("email")} value={transaction.customer_email} />
                  <InfoRow label={t("phone")} value={transaction.customer_phone} />
                </div>
              </div>

              {transaction.type === "withdrawal" && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">{t("bankDetails")}</h3>
                  <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-100">
                    <InfoRow label={t("bank")} value={transaction.bank_code} />
                    <InfoRow label={t("account")} value={transaction.bank_account} />
                  </div>
                </div>
              )}

              <div>
                <h3 className="font-medium text-slate-900 mb-2">{t("callbackStatus")}</h3>
                <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-100">
                  <InfoRow label={t("status")} value={translateCallbackStatus(transaction.callback_status)} />
                  <InfoRow label={t("attempts")} value={transaction.callback_attempts || 0} />
                  <InfoRow 
                    label={t("lastAttempt")} 
                    value={transaction.callback_last_attempt ? format(new Date(transaction.callback_last_attempt), "MMM d, HH:mm:ss") : "N/A"} 
                  />
                </div>
              </div>

              <div>
                <h3 className="font-medium text-slate-900 mb-2">{t("timestamps")}</h3>
                <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-100">
                  <InfoRow 
                    label={t("createdAt")} 
                    value={transaction.created_date ? format(new Date(transaction.created_date), "MMM d, yyyy HH:mm:ss") : "N/A"} 
                  />
                  <InfoRow 
                    label={t("processedAt")} 
                    value={transaction.processed_at ? format(new Date(transaction.processed_at), "MMM d, yyyy HH:mm:ss") : "N/A"} 
                  />
                  <InfoRow 
                    label={t("completedAt")} 
                    value={transaction.completed_at ? format(new Date(transaction.completed_at), "MMM d, yyyy HH:mm:ss") : "N/A"} 
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Actions - Only for transactions */}
        {!isQRCode && (
          <div className="flex gap-3 mt-6 pt-6 border-t">
            {(transaction.status === "failed" || transaction.status === "expired") && (
              <Button onClick={() => onRetry?.(transaction)} className="flex-1 gap-2">
                <RefreshCw className="h-4 w-4" />
                {t("retryTransaction")}
              </Button>
            )}
            {transaction.callback_status === "failed" && (
              <Button variant="outline" onClick={() => onResendCallback?.(transaction)} className="flex-1 gap-2">
                <Send className="h-4 w-4" />
                {t("resendCallback")}
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}